import axios from "axios";
import { v4 as uuid } from "uuid";
import { productCardTemplate } from "./_productCardTemplate";

export class CriteoIntegration {
  _isActive = window.criteo.isActive;
  #debug = true; // Debug mode flag
  #storageKeys = {
    retailerVistorId: window.mmkt.config.criteoAPI.storageKeys.retailerVistorId,
  };
  #partnerId = window.mmkt.config.criteoAPI.partnerId;
  #moreDetailsPageLink =
    "https://canarias.mediamarkt.es/pages/faq-productospatrocinados"; // CAN
  // #moreDetailsPageLink = "https://mediamarkt.pt/pages/produtos-patrocinados "; // PT
  #shopiApiKey = window.mmkt.config.shopifyApiKey;
  #carousel;
  #retailerVisitorIdCookieName = "criteo-retailer-visitor-id";
  #selectors = {
    container: "#criteo-owl-carousel",
    carousel: "#criteo-owl",
    item: ".owl-carousel .owl-item .item",
    cardTitle: "a.product-name",
    cardImage: "img.replace-2x.img-responsive",
    cardSecondImage: ".product-additional img.replace-2x.img-responsive",
    price: ".content_price",
    link: "a.product_img_link",
    titleLink: "a.product-name",
  };
  #card;

  /**
   * Constructor to initialize debug mode
   * @param {boolean} debug - Enable/disable debug logs
   */
  constructor(debug = false) {
    this.#debug = debug;
  }

  /**
   * Centralized logging method
   * @param {string} message - Log message
   * @param {any} data - Optional data to log
   */
  #log(message, data = null) {
    if (!this.#debug) return;

    if (data !== null) {
      console.log(`[Criteo Integration] ${message}`, data);
    } else {
      console.log(`[Criteo Integration] ${message}`);
    }
  }

  #cardTemplate() {
    if (this.#card) return this.#card.cloneNode(true);

    const div = document.createElement("div");

    div.classList.add("item");

    div.innerHTML = productCardTemplate;

    this.#card = div;

    return this.#card;
  }

  /**
   * @returns {string}
   * @description A unique unauthenticated UserID that is persistent across sessions on the same device.
   * needed for Criteo API params
   */
  #retailerVistorId() {
    let visitorId = "";
    let name = `${this.#retailerVisitorIdCookieName}=`;

    const decodedCookie = decodeURIComponent(document.cookie);
    const ca = decodedCookie.split(";");

    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) == " ") {
        c = c.substring(1);
      }
      if (c.indexOf(name) == 0) {
        visitorId = c.substring(name.length, c.length);
      }
    }

    if (!visitorId) {
      visitorId = uuid();
      let date = new Date();
      date.setDate(date.getDate() + 90);

      document.cookie = `${
        this.#retailerVisitorIdCookieName
      }=${visitorId}; expires=${date.toUTCString()}; path=/`;
    }

    return visitorId;
  }

  /**
   * @type {import("./defs").baseReq}
   */
  async _baseRequest(eventType, restOfParams, intraDayUpdate = false) {
    const device = screen.width <= 750 ? "Mobile" : "Desktop";
    const customerId = window?.BKCustomer?.id || window?.customer_id;

    const { data } = await axios({
      method: "GET",
      baseURL: window.mmkt.config.criteoAPI.baseURL,
      params: {
        "criteo-partner-id": this.#partnerId,
        "event-type": eventType,
        "page-id": intraDayUpdate
          ? "IntradayUpdate"
          : `${eventType}Api${device}`,
        "retailer-visitor-id": intraDayUpdate
          ? "intraday-update-stock"
          : this.#retailerVistorId(),
        ...(mmkt.core.cookie
          .getCookie("cookieconsent_preferences_disabled")
          ?.includes("marketing") && { block: 1 }),
        ...(!intraDayUpdate && { environment: device[0].toLowerCase() }),
        ...(customerId && !intraDayUpdate && { "customer-id": customerId }),
        ...restOfParams,
      },
    });

    return data;
  }

  /**
   * @param {HTMLElement} parent
   */
  _sponsoredBadge(parent) {
    const {
      badge: { title, msg, link },
    } = window.criteo;

    const sponsoredContainer = document.createElement("div");

    sponsoredContainer.classList.add("sponsored-criteo");

    sponsoredContainer.innerHTML = `
    <div class="sponsored-criteo-container">
      <span class="sponsored-criteo-message">
        ${title}
      </span>
      <div class="sponsored-criteo-icon-container">
        <svg width="32" height="32" viewBox="0 0 24 24">
          <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2Zm0 18a8 8 0 1 1 8-8 8 8 0 0 1-8 8Zm1-7.5a.5.5 0 0 0-.5-.5h-1a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h1a.5.5 0 0 0 .5-.5Zm0-4a.5.5 0 0 0-.5-.5h-1a.5.5 0 0 0-.5.5v1a.5.5 0 0 0 .5.5h1a.5.5 0 0 0 .5-.5Z">
          </path>
        </svg>
        <div class="hide tooltip-box">
          <svg class="criteo-close" fill="#fff" height="10px" width="10px" version="1.1" id="Capa_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 460.775 460.775" xml:space="preserve">
            <path d="M285.08,230.397L456.218,59.27c6.076-6.077,6.076-15.911,0-21.986L423.511,4.565c-2.913-2.911-6.866-4.55-10.992-4.55 c-4.127,0-8.08,1.639-10.993,4.55l-171.138,171.14L59.25,4.565c-2.913-2.911-6.866-4.55-10.993-4.55 c-4.126,0-8.08,1.639-10.992,4.55L4.558,37.284c-6.077,6.075-6.077,15.909,0,21.986l171.138,171.128L4.575,401.505 c-6.074,6.077-6.074,15.911,0,21.986l32.709,32.719c2.911,2.911,6.865,4.55,10.992,4.55c4.127,0,8.08-1.639,10.994-4.55 l171.117-171.12l171.118,171.12c2.913,2.911,6.866,4.55,10.993,4.55c4.128,0,8.081-1.639,10.992-4.55l32.709-32.719 c6.074-6.075,6.074-15.909,0-21.986L285.08,230.397z"/>
          </svg>
          <p>
            ${msg}
          </p>
          <a href="${this.#moreDetailsPageLink}" target="_BLANK">
            ${link}
          </a>
        </div>
      </div>
    </div>
    `;

    sponsoredContainer
      .querySelector(".tooltip-box > svg")
      .addEventListener("click", (_e) =>
        sponsoredContainer.querySelector(".tooltip-box").classList.add("hide"),
      );

    sponsoredContainer.querySelector(".tooltip-box > svg").addEventListener(
      "touchend",
      (e) => {
        e.preventDefault();
        sponsoredContainer.querySelector(".tooltip-box").classList.add("hide");
      },
      { passive: false },
    );

    sponsoredContainer
      .querySelector(".sponsored-criteo-icon-container > svg")
      .addEventListener("click", (_e) =>
        sponsoredContainer
          .querySelector(".tooltip-box")
          .classList.toggle("hide"),
      );

    sponsoredContainer
      .querySelector(".sponsored-criteo-icon-container > svg")
      .addEventListener(
        "touchend",
        (e) => {
          e.preventDefault();
          sponsoredContainer
            .querySelector(".tooltip-box")
            .classList.toggle("hide");
        },
        { passive: false },
      );

    parent.appendChild(sponsoredContainer);
  }

  /**
   * @type {import("./defs").getShopiProductsReq}
   */
  async _getProducts(SKUs) {
    const query = `query:\\"${SKUs.join(" OR ")}\\"`;

    const {
      data: {
        data: {
          products: { nodes: shopifyProducts },
        },
      },
    } = await axios(
      `https://mediamarkt-canarias.myshopify.com/api/2023-01/graphql.json`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": this.#shopiApiKey,
        },
        data: `{ "query": "{ products(first: 50, ${query}) { nodes { id description availableForSale collections(first: 100) { nodes { title } } variants(first: 10) { nodes { sku } } onlineStoreUrl title vendor tags featuredImage { src } priceRange { minVariantPrice { amount currencyCode } } compareAtPriceRange { minVariantPrice { amount currencyCode } } } } }" }`,
      },
    );

    const { inStock, outStock } = shopifyProducts.reduce(
      (prev, product) => {
        prev[product.availableForSale ? "inStock" : "outStock"].push(product);

        return prev;
      },
      { inStock: [], outStock: [] },
    );

    outStock.forEach(async (product) => {
      try {
        const price = Number(product.priceRange.minVariantPrice.amount);
        const compareAtPrice = Number(
          product.compareAtPriceRange.minVariantPrice.amount,
        );

        const params = {
          item: product.variants.nodes[0].sku,
          price,
          "list-price": compareAtPrice > 0 ? compareAtPrice : price,
          availability: 0,
        };

        await this._baseRequest(
          "viewItem",
          {
            ...params,
          },
          true,
        );
      } catch (error) {
        console.log(error);
      }
    });

    return inStock;
  }

  /**
   * @param {HTMLElement} el
   */
  #isInViewport(el) {
    const rect = el.getBoundingClientRect();
    const viewHeight =
      window.innerHeight || document.documentElement.clientHeight;
    const viewWidth = window.innerWidth || document.documentElement.clientWidth;

    const visibleH = Math.min(rect.bottom, viewHeight) - Math.max(rect.top, 0);
    const visibleW = Math.min(rect.right, viewWidth) - Math.max(rect.left, 0);

    if (visibleH <= 0 || visibleW <= 0) return false;

    const visibleArea = visibleH * visibleW;
    const elementArea = rect.width * rect.height;

    return elementArea > 0 && visibleArea / elementArea >= 0.5;
  }

  /**
   * @param {HTMLElement[]} elements
   */
  _viewPortEvent(elements) {
    this.#log(
      "[OnView] Setting up viewport tracking for elements:",
      elements.length,
    );

    const restOfEl = elements.filter((el) => {
      if (this.#isInViewport(el) && !el.getAttribute("viewport-visible")) {
        let timer = setTimeout(() => {
          if (this.#isInViewport(el)) {
            el.setAttribute("viewport-visible", "true");
            const beacon = el.dataset.criteoViewBeacon;
            const placementBeacon = el.dataset.criteoPlacementViewBeacon;
            if (placementBeacon) this.#sendBeacon(placementBeacon);
            if (beacon) this.#sendBeacon(beacon);
          }
        }, 1000);
        return false;
      }
      return true;
    });

    document.addEventListener(
      "scroll",
      () => {
        restOfEl.forEach((el) => {
          if (el.getAttribute("viewport-visible")) return;

          if (this.#isInViewport(el)) {
            if (!el._viewTimer) {
              el._viewTimer = setTimeout(() => {
                if (this.#isInViewport(el)) {
                  el.setAttribute("viewport-visible", "true");
                  const beacon = el.dataset.criteoViewBeacon;
                  const placementBeacon = el.dataset.criteoPlacementViewBeacon;
                  if (placementBeacon) this.#sendBeacon(placementBeacon);
                  if (beacon) this.#sendBeacon(beacon);
                }
                el._viewTimer = null;
              }, 1000);
            }
          } else {
            clearTimeout(el._viewTimer);
            el._viewTimer = null;
          }
        });
      },
      { passive: true },
    );
  }

  /**
   * @param {string[]} beacons
   */
  _onLoadEvent(beacons) {
    this.#log("[OnLoad] Sending load beacons:", beacons.length);
    beacons.forEach((beacon) => {
      if (beacon) {
        this.#log("[OnLoad] Beacon:", beacon);
        this.#sendBeacon(beacon);
      }
    });
  }

  /**
   * @param {HTMLElement[]} elements
   */
  _onClickEvent(elements) {
    this.#log(
      "[OnClick] Setting up click tracking for elements:",
      elements.length,
    );
    elements.forEach((el) => {
      this.#log("[OnClick] Element with beacon:", {
        element: el,
        beacon: el.dataset.criteoOnClickBeacon,
      });
      el.addEventListener(
        "click",
        (e) => {
          const beacon = e.currentTarget.dataset.criteoOnClickBeacon;
          this.#log("[OnClick] Element clicked", e.currentTarget);
          if (beacon) {
            this.#log("[OnClick] Sending click beacon");
            this.#sendBeacon(beacon);
          }
        },
        { passive: true },
      );

      el.addEventListener(
        "auxclick",
        (e) => {
          const beacon = e.currentTarget.dataset.criteoOnClickBeacon;
          if (beacon) this.#sendBeacon(beacon);
        },
        { passive: true },
      );
    });
  }

  /**
   * @param {string} beacon
   */
  #sendBeacon(beacon) {
    try {
      this.#log("[Beacon] Sending:", beacon);
      // Use navigator.sendBeacon for reliable delivery during page navigation
      // Falls back to fetch with keepalive for broader compatibility
      if (navigator.sendBeacon) {
        navigator.sendBeacon(beacon);
        this.#log("[Beacon] ✓ Sent via navigator.sendBeacon");
      } else {
        fetch(beacon, { method: "GET", keepalive: true });
        this.#log("[Beacon] ✓ Sent via fetch with keepalive");
      }
    } catch (error) {
      this.#log("[Beacon] ✗ Failed to send:", error);
    }
  }

  _formatPrice(price) {
    price = price.toString();

    const zeroDecimals = toString().includes(".00");

    price = price.replace("€", "");
    price = price.replace(",", "");
    price = price.replace(".00", "");

    price = price.concat(zeroDecimals ? ",- €" : " €");

    return price;
  }

  /**
   * @param {number} price
   * @param {number} compareAt
   */
  #priceMarkup(price, compareAt) {
    const priceFormated = this._formatPrice(price);
    const compareFormated = this._formatPrice(compareAt);

    return `
      <div class="content_price">
        ${
          compareAt > price
            ? `<span class="compare-at-price">${compareFormated}</span>`
            : ""
        }
        <span class="regular-price ${compareAt > price ? `sale-price` : ""}">
          ${priceFormated}
        </span>
      </div>`;
  }

  async _renderCarousel(products, OnViewBeacon, OnLoadBeacon) {
    try {
      this.#carousel = document.querySelector(this.#selectors.carousel);

      if (products.length) {
        const criteoProductsSKUs = products.map((product) => product.ProductId);

        // retrieve product info from Shopify and merge it with the info of criteo
        /**
         * @type {(import("./defs").IShopiProduct & { criteo: import("./defs").Product })[]}
         */
        const shopifyProducts = (
          await this._getProducts(criteoProductsSKUs)
        ).map((product) => {
          const sameProduct = products.find((prod) =>
            product.tags.join(" ").includes(prod.ProductId),
          );

          return { ...product, criteo: sameProduct };
        });

        const cards = [];

        shopifyProducts.forEach((shopiProduct) => {
          const criteoProduct = shopiProduct.criteo;
          const clonedCard = this.#cardTemplate();
          const price = parseFloat(
            shopiProduct.priceRange.minVariantPrice.amount,
          );
          const compareAt = parseFloat(
            shopiProduct.compareAtPriceRange.minVariantPrice.amount,
          );

          clonedCard.querySelector(this.#selectors.cardTitle).innerText =
            shopiProduct.title;

          clonedCard.querySelector(this.#selectors.cardImage).src =
            shopiProduct.featuredImage?.src;

          clonedCard.querySelector(this.#selectors.cardImage).alt =
            shopiProduct.title;

          clonedCard.querySelector(this.#selectors.cardSecondImage) &&
            ((clonedCard.querySelector(this.#selectors.cardSecondImage).src =
              shopiProduct.featuredImage?.src),
            (clonedCard.querySelector(this.#selectors.cardSecondImage).alt =
              shopiProduct.title));

          clonedCard.querySelector(this.#selectors.price).innerHTML =
            this.#priceMarkup(price, compareAt);

          clonedCard.querySelector(this.#selectors.link).href =
            shopiProduct.onlineStoreUrl;

          clonedCard.querySelector(this.#selectors.link).title =
            shopiProduct.title;

          clonedCard.querySelector(this.#selectors.titleLink).href =
            shopiProduct.onlineStoreUrl;

          clonedCard.querySelector(this.#selectors.titleLink).title =
            shopiProduct.title;

          const bvContainer =
            clonedCard.querySelector('[data-bv-show="rating_summary"]') ||
            clonedCard.querySelector('[data-bv-show="inline_rating"]');

          if (bvContainer) {
            const sku = shopiProduct.variants?.nodes?.[0]?.sku;

            bvContainer.setAttribute("data-bv-product-id", sku || "");
            bvContainer.setAttribute(
              "data-bv-redirect-url",
              shopiProduct.onlineStoreUrl || "",
            );
          }

          clonedCard.dataset.criteoViewBeacon = criteoProduct?.OnViewBeacon;

          clonedCard.dataset.criteoOnClickBeacon = criteoProduct?.OnClickBeacon;

          cards.push(clonedCard);
        });

        this.#carousel.dataset.criteoViewBeacon = OnViewBeacon;

        this._viewPortEvent([...cards, this.#carousel]);
        this._onClickEvent(cards);

        const onLoadBeacons = shopifyProducts.map(
          (prod) => prod.criteo?.OnLoadBeacon,
        );

        this._onLoadEvent([...onLoadBeacons, OnLoadBeacon]);

        cards.forEach((card) => this.#carousel.appendChild(card));

        const observer = new MutationObserver((mutations) => {
          if (
            !!mutations.find(
              (mutation) =>
                mutation.type === "attributes" &&
                mutation.attributeName === "class",
            )
          ) {
            this._viewPortEvent(cards);
          }
        });

        observer.observe(this.#carousel, { attributes: true, subtree: true });

        return cards.length;
      } else {
        document.querySelector(this.#selectors.container).style.display =
          "none";
      }
    } catch (error) {
      console.log(error);
    }
  }

  _isMarketingBlocked() {
    if (window.Shopify?.customerPrivacy) {
      const consent = window.Shopify.customerPrivacy.currentVisitorConsent();
      return consent.marketing === "no" || !consent.marketing;
    }
    // Fallback: mmkt custom cookie
    return !!mmkt.core.cookie
      .getCookie("cookieconsent_preferences_disabled")
      ?.includes("marketing");
  }

  _hasCampaignConfigured() {
    // PLP/Search: only if criteoCategory metafield is set
    if (window.criteoCategory && window.criteoCategory !== "") return true;
    // Home, Basket, PDP: always have Criteo carousel configured
    if (document.querySelector("#criteo-owl")) return true;
    return false;
  }

  // Show cookie consent banner in grid or carousel context
  _showCookieBanner(context, mode = "grid") {
    if (document.getElementById("criteo-cookie-banner")) return;
    if (!context) return;

    const template = document.getElementById("criteo-cookie-banner-template");
    if (!template) return;

    const fragment = template.content.cloneNode(true);
    const banner = fragment.querySelector("#criteo-cookie-banner");

    banner
      .querySelector(".js-criteo-cookie-activate")
      ?.addEventListener("click", () => {
        window.__cqConsent("all_accepted");
        setTimeout(() => window.location.reload(), 400);
      });

    banner
      .querySelector(".js-criteo-cookie-more-info")
      ?.addEventListener("click", (e) => {
        e.preventDefault();
        document.querySelector(".cq-btn")?.click();
      });

    if (mode === "carousel") {
      // Hide the owl carousel, insert banner in its place inside the container
      const owlContainer = context.querySelector(".carousel-criteo-container");
      if (owlContainer) {
        owlContainer.style.display = "none";
        context.querySelector(".block_content")?.appendChild(banner);
      } else {
        context.parentElement.insertBefore(banner, context);
      }
    } else {
      context.parentElement.insertBefore(banner, context);
    }
  }
}
